import Missiles from "../../model/skillTree/missile/Missiles";
import { GervaisSpriteNames } from "../../model/sprite/GervaisSpriteNames";
import { Settings } from "../../Settings";
import SkillRenderer from "./SkillRenderer";

export default class MissilesSkillRenderer extends SkillRenderer {
    /**
     * 
     * @param {Missiles[]} missilesArray 
     * @param {GervaisAnimatedSpritesheet} spellFxSpritesheet 
     */
    constructor(missilesArray, spellFxSpritesheet) {
        super();

        /**
         * @constant
         * @type {Missiles[]}
         */
        this.missilesArray = missilesArray;
        /**
         * @constant
         * @type {GervaisAnimatedSpritesheet}
         */
        this.spellFxSpritesheet = spellFxSpritesheet;

        /**
         * The sprite animation used for the Missiles skill.
         * @private
         * @type {SpriteAnimation}
         */
        this.missilesSkillAnimation = null;
        /**
         * @type {number}
         */
        this.skillStart = 0;
        /**
         * The sprite animation used for the Missiles skill.
         * @private
         * @type {SpriteAnimation}
         */
        this.missilesExplosionAnimation = null;
    }

    /**
     * @override
     * @param {CanvasRenderingContext2D} context 
     * @param {Projection} projection 
     */
    renderSkill(context, projection) {
        if (this.missilesArray.length === 0) {
            return;
        }

        const now = Date.now();
        if (now - this.skillStart > 100000) {
            this.skillStart = now; // bad things happen in the sprite animation manager if the start time is from 1970.
        }

        // Make sure the sprite is initialized.
        if (!this.missilesSkillAnimation) {
            this.missilesSkillAnimation = this.spellFxSpritesheet.getSpriteAnimation(GervaisSpriteNames.MISSILE_EFFECT_NAME_FAT_ARROW);
            this.missilesExplosionAnimation = this.spellFxSpritesheet.getSpriteAnimation(GervaisSpriteNames.DAMAGE_EFFECT_NAME_BLUE_DAMAGE);
        }


        // Save the line width.
        const safeLineWidth = context.lineWidth;

        context.lineWidth = 4;
        context.strokeStyle = '#FFA1';

        for (let i = 0; i < this.missilesArray.length; i++) {
            this.renderMissiles(context, projection, this.missilesArray[i]);
        }

        // Restore the line width
        context.lineWidth = safeLineWidth;
    }
    
    /**
     * @private
     * @param {CanvasRenderingContext2D} context 
     * @param {Projection} projection 
     * @param {Missiles} missiles 
     */
    renderMissiles(context, projection, missiles) {

        if (!missiles.skillActive) {
            return;
        }

        const missilesArray = missiles.missiles;
        for (let i = 0; i < missilesArray.length; i++) {
            const missile = missilesArray[i];
            if (!missile || !missile.active) {
                continue;
            }

            const start = missile.startPosition;
            const pos = missile.position;

            //////////////////////////////////////
            // Draw a line on the missile path
            //////////////////////////////////////

            context.beginPath();

            projection.worldToScreen(start, this.screenCoordinate);
            context.moveTo(this.screenCoordinate.x, this.screenCoordinate.y);
            
            this.screenCoordinate.copy(pos);
            projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
            context.lineTo(this.screenCoordinate.x, this.screenCoordinate.y);

            context.stroke();

            ///////////////////////////////////////////
            // Draw a sprite at the end point
            ///////////////////////////////////////////

            this.screenCoordinate.copy(pos);
            this.screenCoordinate.subtractXY(Settings.tile.halfSize, Settings.tile.halfSize);

            projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);

            const sprite = this.missilesSkillAnimation.getDirectionSprite(missile.spriteDirection);

            this.renderSprite(context, projection, sprite, this.screenCoordinate);
        }

        const current = Date.now();

        // Render explosions
        const explosions = missiles.missileExplosions;
        for (let i = 0; i < explosions.length; i++) {
            const explosion = explosions[i];
            if (!explosion.active) {
                continue;
            }

            const sprite = this.missilesExplosionAnimation.getSlowCurrentSpriteAsOfNoLooping(current, explosion.time);
            if (!sprite) {
                // If the explosion sprite is done, then mark the explosion as inactive.
                explosion.deactivate();
                continue;
            }

            this.screenCoordinate.copy(explosion.position);
            this.screenCoordinate.subtractXY(Settings.tile.halfSize, Settings.tile.halfSize);
            projection.worldToScreen(this.screenCoordinate, this.screenCoordinate);
            this.renderSprite(context, projection, sprite, this.screenCoordinate);
        }
    }

}